import React from 'react';

import FlowOverviewPage from './capitalize-rollovers/FlowOverviewPage';
import EmailConfirmationSuccess from './EmailConfirmationSuccess';
import ActionMenu from './Shared/components/ActionMenu';
import GoalImageIconPair from './Shared/components/GoalImages/GoalImageIconPair';
import { FormikGoalImageSelector as GoalImageSelector } from './Shared/components/GoalImages/GoalImageSelector';
import HeaderActionMenu from './Shared/components/HeaderActionMenu';
import UserAgreementContent from './Shared/components/UserAgreementContent';

interface ComponentRegistry {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: React.ComponentType<React.PropsWithChildren<any>>;
}

const componentRegistry: ComponentRegistry = {
  ActionMenu,
  EmailConfirmationSuccess,
  GoalImageIconPair,
  GoalImageSelector,
  HeaderActionMenu,
  UserAgreementContent,
  FlowOverviewPage,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LazyApp = () => Promise<{ default: React.ComponentType<React.PropsWithChildren<any>> }>;

interface AppRegistry {
  [key: string]: LazyApp;
}

const appRegistry: AppRegistry = {
  ActiveEmployerContributionConfigurationApp: () => import('./apps/ActiveEmployerContributionConfigurationApp'),
  AccountAccessBannerApp: () => import('./apps/AccountAccessBannerApp'),
  AddGoalAccountNameForm: () => import('./apps/AddGoal/AccountNameForm'),
  AddGoalAccountOwnershipSelection: () => import('./apps/AddGoal/AccountOwnershipSelection'),
  AddGoalAutoAdjustSelection: () => import('./apps/AddGoal/AutoAdjustSelection'),
  AddGoalFlowPortfolioHoldings: () => import('./apps/AddGoalFlowPortfolioHoldingsApp'),
  AdviceInitiationFlow: () => import('./apps/AdviceInitiationFlowApp'),
  AllocationSelection: () => import('./apps/AllocationSelectionApp'),
  AutomaticDepositConfig: () => import('./apps/AutomaticDepositConfigApp'),
  AutomaticWithdrawalConfig: () => import('./apps/AutomaticWithdrawalConfigApp'),
  AgreementsApp: () => import('./apps/AgreementsApp'),
  BankAccountSetup: () => import('./apps/BankAccountSetupApp'),
  Article: () => import('./apps/ArticleApp'),
  CheckingBankAccountSetup: () => import('./apps/CheckingBankAccountSetupApp'),
  Confirmation: () => import('./apps/AddGoal/confirmation/Confirmation'),
  DepositSetup: () => import('./apps/DepositSetupApp'),
  DirectDepositFields: () => import('./apps/DirectDepositFieldsApp'),
  DocumentVerification: () => import('./apps/DocumentVerificationApp'),
  AdvisedSignup401kAgreementSignature: () => import('./apps/AdvisedSignup401kAgreementSignatureApp'),
  EditBeneficiaries: () => import('./apps/Beneficiaries/EditBeneficiariesPage'),
  EditCheckingBeneficiaries: () => import('./apps/EditCheckingBeneficiariesApp'),
  EditGoalProfileFlowApp: () => import('./apps/EditGoalProfileFlowApp'),
  EmergencyFundCustomCoverageApp: () => import('./apps/AddGoal/EmergencyFundCustomCoverageApp'),
  EmployeeOnboardingFlowApp: () => import('./apps/EmployeeOnboardingFlowApp'),
  ExistingUserEnrollmentApp: () => import('./apps/ExistingUserEnrollmentApp'),
  EmployeeOnboardingCreatePasswordApp: () => import('./apps/EmployeeOnboardingCreatePasswordApp'),
  EmployeeOnboardingIdentityVerificationApp: () => import('./apps/EmployeeOnboardingIdentityVerificationApp'),
  EndOfYearAttestationApp: () => import('./apps/EndOfYearAttestationApp'),
  EstimatedStudentLoanPaymentFlow: () => import('./apps/EstimatedStudentLoanPaymentFlowApp'),
  ExternalAccountAssociation: () => import('./apps/ExternalAccountAssociationApp'),
  ExternalAccountSetup: () => import('./apps/ExternalAccountSetupApp'),
  FakeOauthClient: () => import('./apps/FakeOauthClientApp'),
  FirmTermsAcceptance: () => import('./apps/FirmTermsAcceptanceApp'),
  FiveTwoNineAccountDetails: () => import('./apps/FiveTwoNineAccountDetailsApp'),
  FiveTwoNineConnectUnite: () => import('./apps/FiveTwoNineConnectUniteApp'),
  FiveTwoNinePortfolioSelection: () => import('./apps/FiveTwoNinePortfolioSelectionApp'),
  FiveTwoNineSignup: () => import('./apps/FiveTwoNineSignupApp'),
  FourOhOneKContributionsOverviewCard: () => import('./apps/FourOhOneKContributionsOverviewCardApp'),
  FourOhOneKDeferralSettings: () => import('./apps/FourOhOneKDeferralSettingsApp'),
  FourOhOneKEmployerMatchGoalCard: () => import('./apps/FourOhOneKEmployerMatchGoalCardApp'),
  GoalOverviewGuide: () => import('./apps/GoalOverviewGuideApp'),
  GoalOverviewSecondary: () => import('./apps/GoalOverviewSecondaryApp'),
  InAppEducationApp: () => import('./apps/InAppEducationApp'),
  InAppMessagesDisclosure: () => import('./apps/InAppMessagesDisclosureApp'),
  IncompleteRolloverReminder: () => import('./apps/IncompleteRolloverReminderApp'),
  InvestingJournal: () => import('./apps/InvestingJournal'),
  IraContributionsOverviewCard: () => import('./apps/IraContributionsOverviewCardApp'),
  IraRecharacterizationFlow: () => import('./apps/IraRecharacterizationFlowApp'),
  LegalAccountNumberRow: () => import('./apps/LegalAccountNumberRow'),
  LegalNameChangeFlow: () => import('./apps/LegalNameChangeFlowApp'),
  LimitedAdviceAllocationSelection: () => import('./apps/LimitedAdviceAllocationSelectionApp'),
  MessageCard: () => import('./InAppMessages/MessageCard'),
  MultidocUploadFlow: () => import('./apps/MultidocUploadFlowApp'),
  PayrollDeductionSetupApp: () => import('./apps/PayrollDeductionSetupApp'),
  PlaidItemUpdate: () => import('./apps/PlaidItemUpdateApp'),
  PlaidLink: () => import('./apps/PlaidLinkApp'),
  PerformanceRealtimeLoader: () => import('./apps/PerformanceRealtimeLoaderApp'),
  PortfolioDonut: () => import('./apps/PortfolioDonutApp'),
  PreferredNameChangeFlow: () => import('./apps/PreferredNameChangeFlowApp'),
  RetirementAgeForm: () => import('./apps/AddGoal/IRA/RetirementAgeForm'),
  RecurringTransferConfigApp: () => import('./apps/RecurringTransferConfigApp'),
  RecurringTransactionMessage: () => import('./apps/RecurringTransactionMessageApp'),
  RewardsContentCardApp: () => import('./apps/RewardsContentCardApp'),
  RewardsCenterContentCardApp: () => import('./apps/RewardsCenterContentCardApp'),
  DismissibleBanner: () => import('./apps/DismissibleBannerApp'),
  SetDeferralRateBanner: () => import('./apps/SetDeferralRateBannerApp'),
  SettingPricingCardApp: () => import('./apps/SettingPricingCardApp'),
  SignupFlow: () => import('./apps/SignupFlowApp'),
  Solo401kPrequalification: () => import('./apps/Solo401kPrequalificationApp'),
  SoloFourOhOneKSettings: () => import('./apps/SoloFourOhOneKSettingsApp'),
  SomebodysExternalAccountUpdateConfirmationApp: () =>
    import('./apps/SomebodysExternalAccountDetail/SomebodysExternalAccountUpdateConfirmationApp'),
  SubAccountTransferSetup: () => import('./apps/SubAccountTransferSetupApp'),
  TaxCoordinationInfoModal: () => import('./apps/TaxCoordinationInfoModalApp'),
  UserProfileSetupFlow: () => import('./apps/UserProfileSetupFlowApp'),
  WithdrawalSetup: () => import('./apps/WithdrawalSetupApp'),
  OnboardingFlow: () => import('./apps/OnboardingFlowApp'),
  TransferredUserOnboardingApp: () => import('./apps/TransferredUserOnboardingApp'),
  GenericErrorApp: () => import('./apps/GenericErrorApp'),
  TrustNonDomesticBlocked: () => import('./apps/AddGoal/TrustNonDomesticBlocked'),
  TrustSelectionForm: () => import('./apps/AddGoal/TrustSelectionForm'),
  TrustedContactSectionApp: () => import('./TrustedContactSectionApp/TrustedContactSectionApp'),
  RewardDetails: () => import('./apps/RewardDetailsApp'),
};

const retry = (fn: LazyApp, retriesLeft = 5, interval = 1000) => {
  return new Promise<{ default: React.ComponentType }>((resolve, reject) => {
    fn()
      .then(resolve)
      .catch((error: Error) => {
        if (retriesLeft === 0) {
          reject(error);
          return;
        }

        setTimeout(() => {
          retry(fn, retriesLeft - 1, interval).then(resolve, reject);
        }, interval);
      });
  });
};

const getApp = (name: string) => React.lazy(() => retry(appRegistry[name]));

const getComponent = (name: string) => componentRegistry[name];

export default {
  getApp,
  getComponent,
};
